import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import shared from './GarageContent.module.scss';
import card from '../DealerStockContent/DealerStockCard.module.scss';
import heart from '../DealerStockContent/DealerFavoriteButton.module.scss';
import Loading from '../Layout/Loading';
import EmptyContentCard from '../Layout/EmptyContentCard';
import AuctionCarCard from './AuctionCarCard';
import { getCarFavourites, updateCarFavourite } from '../../services';
import { setCarDetail } from '../../store/reducers/carReducer';

// Where the car page's Back link returns to.
const BACK_PATH = '/garage?source=auction&tab=favorites';

/**
 * One saved auction car, in the same card as a saved dealer-stock car, with
 * the heart that takes it out of the garage.
 */
const AuctionSavedCard = ({ car, onRemove, removing }) => {
    const dispatch = useDispatch();
    // The detail page reads the car from the store when one is set; clear it
    // so the page loads this car fresh.
    const linkProps = { state: { from: BACK_PATH }, onClick: () => dispatch(setCarDetail(null)) };

    return (
        <AuctionCarCard
            car={car}
            linkProps={linkProps}
            overlay={(
                <button
                    type="button"
                    className={`${heart.button} ${heart.active} ${card.favoriteButton}`}
                    onClick={() => onRemove(car)}
                    disabled={removing}
                    aria-label="Remove from my garage"
                    title="Remove from my garage"
                >
                    <i className="fas fa-heart" aria-hidden="true"></i>
                </button>
            )}
        />
    );
};

/**
 * My Garage > Auction > Saved Cars: the auction cars the member hearted, as
 * the same cards the Dealer stock side uses.
 */
const AuctionSavedCars = () => {
    const [cars, setCars] = useState(null);
    const [failed, setFailed] = useState(false);
    const [removing, setRemoving] = useState(null);

    useEffect(() => {
        let active = true;
        // The API caps a page at 100, more than anyone keeps saved.
        getCarFavourites('pageNo=1&size=100')
            .then((res) => {
                if (!active) return;
                if (res?.message !== 'Success') throw new Error(res?.message || 'Saved cars unavailable');
                setCars(Array.isArray(res.data) ? res.data : []);
            })
            .catch(() => active && setFailed(true));
        return () => { active = false; };
    }, []);

    const remove = (car) => {
        setRemoving(car._id);
        setCars((list) => list.filter((c) => c._id !== car._id));
        updateCarFavourite({ carId: car._id })
            .then((res) => {
                if (res?.status !== 201) throw new Error('Not removed');
            })
            .catch(() => setCars((list) => [car, ...list]))
            .finally(() => setRemoving(null));
    };

    if (failed) {
        return <div className={shared.error}>Could not load your saved auction cars right now. Please try again in a moment.</div>;
    }
    if (cars === null) {
        return <div className={shared.state}><Loading text="Saved Cars" /></div>;
    }
    if (cars.length === 0) {
        return (
            <div className={shared.state}>
                <EmptyContentCard
                    header="No saved auction cars yet"
                    message="Tap the heart on any auction car to keep it here, with its lot, grade and auction date."
                />
                <Link to="/auction" className={shared.primaryLink}>Browse Auctions</Link>
            </div>
        );
    }
    return (
        <div className={shared.grid}>
            {cars.map((car) => (
                <AuctionSavedCard key={car._id} car={car} onRemove={remove} removing={removing === car._id} />
            ))}
        </div>
    );
};

export default AuctionSavedCars;
